import { useEffect, useMemo, useRef, useState } from 'react';

import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { toast } from 'sonner';

import {
  LoadFileImportReportRow,
  LoadFileImportRowStatus,
} from '../../../../../../../../../../backend/src/integrations/relativity/load-file-import/load-file-import-types';
import { useDownloadRelativityReportArtefact } from '@/api/mutations/useDownloadRelativityReportArtefact';
import useGetLoadFileImportReport from '@/api/queries/useGetLoadFileImportReport';
import useGetLoadFileImportStatus from '@/api/queries/useGetLoadFileImportStatus';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Progress } from '@/new/components/shadcn/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';
import { cn } from '@/new/lib/utils';

interface ProgressStepProps {
  matterId: string;
  importId: string;
  jobId: string | null;
}

type StatusFilter = 'all' | LoadFileImportRowStatus;

/** Presentation for each per-row status: chip label, colours and icon. */
const STATUS_META: Record<LoadFileImportRowStatus, { label: string; chip: string; icon?: string }> = {
  matched: { label: 'Matched', chip: 'bg-green-50 text-green-500', icon: 'lucide--check' },
  unmatched: { label: 'Unmatched', chip: 'bg-gray-50 text-gray-500', icon: 'lucide--minus' },
  ambiguous: { label: 'Ambiguous', chip: 'bg-amber-50 text-amber-500' },
};

/** A coloured status chip for a report row. */
function StatusChip({ row }: { row: LoadFileImportReportRow }) {
  const meta = STATUS_META[row.status];
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium', meta.chip)}>
      {meta.icon && <Icon className={cn(meta.icon, 'text-[0.9em]')} />}
      {meta.label}
      {row.status === 'ambiguous' && row.candidateCount ? ` · ${row.candidateCount} matches skipped` : ''}
    </span>
  );
}

const columnHelper = createColumnHelper<LoadFileImportReportRow>();

const columns = [
  columnHelper.accessor('matchValue', {
    header: 'Load file identifier',
    cell: (info) => <TruncatedCell value={info.getValue()} className="text-gray-700" />,
  }),
  columnHelper.display({
    id: 'matchedFilename',
    header: 'Matched Wexler file',
    cell: ({ row }) =>
      row.original.status === 'matched' && row.original.matchedFilename ? (
        <TruncatedCell value={row.original.matchedFilename} className="text-gray-700" />
      ) : (
        <span className="text-gray-300">—</span>
      ),
  }),
  columnHelper.display({
    id: 'status',
    header: 'Status',
    meta: { className: 'w-[150px]' },
    cell: ({ row }) => <StatusChip row={row.original} />,
  }),
];

export function ProgressStep({ matterId, importId }: ProgressStepProps) {
  const { counters, byteProgress, isComplete, error } = useGetLoadFileImportStatus(matterId, importId);
  const { fetchDownload: downloadReport, isLoadingDownload: isDownloading } =
    useDownloadRelativityReportArtefact(matterId);
  const [filter, setFilter] = useState<StatusFilter>('all');

  const {
    data: report,
    isLoading: isReportLoading,
    isError: isReportError,
  } = useGetLoadFileImportReport(matterId, importId, {
    enabled: isComplete,
  });

  // Toast only on the running → complete transition, not when deep-linking
  // into an import that already finished.
  const sawRunningRef = useRef(false);
  const hasCounters = counters != null;

  useEffect(() => {
    if (!isComplete) {
      if (hasCounters) sawRunningRef.current = true;
      return;
    }
    if (sawRunningRef.current) {
      sawRunningRef.current = false;
      toast.success('Load file import complete');
    }
  }, [isComplete, hasCounters]);

  // Bytes-based: row counts can't give a percentage (the total row count is
  // unknown while the backend streams the file). Capped at 99 so the bar never
  // claims completion while the report/audit artefact is still being written.
  const percent = useMemo(() => {
    if (isComplete) return 100;
    if (!byteProgress || byteProgress.totalBytes === 0) return 0;
    return Math.min(99, Math.round((byteProgress.bytesRead / byteProgress.totalBytes) * 100));
  }, [byteProgress, isComplete]);

  const filteredRows = useMemo(() => {
    const rows = report?.rows ?? [];
    return filter === 'all' ? rows : rows.filter((row) => row.status === filter);
  }, [report, filter]);

  const table = useReactTable({
    data: filteredRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const processed = (counters?.matched ?? 0) + (counters?.unmatched ?? 0) + (counters?.ambiguous ?? 0);

  const summary = report?.summary ?? counters;

  const filters: { key: StatusFilter; label: string; count: number }[] = [
    { key: 'all', label: 'All load file rows', count: summary?.totalRows ?? 0 },
    { key: 'matched', label: 'Matched', count: summary?.matched ?? 0 },
    { key: 'unmatched', label: 'Unmatched', count: summary?.unmatched ?? 0 },
    { key: 'ambiguous', label: 'Ambiguous', count: summary?.ambiguous ?? 0 },
  ];

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-2xl flex-col gap-8">
      {/* Progress header */}
      <div className="my-10 mb-0 space-y-3.5">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-600">
            {isComplete ? 'Import complete' : `Processing: ${processed.toLocaleString()} rows`}
          </span>
          <span className="font-medium text-gray-900">{percent}%</span>
        </div>
        <Progress value={percent} className="h-1.5" />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-center text-sm text-red-700">{error}</div>
      )}

      {isComplete && !error && (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          {/* Status filter tabs */}
          <div className="flex flex-wrap items-center justify-between">
            <div className="flex flex-wrap">
              {filters.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={cn(
                    'rounded-full px-2 py-1 text-xs transition-colors',
                    filter === f.key ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600',
                  )}
                >
                  {f.label} {f.count.toLocaleString()}
                </button>
              ))}
            </div>
            <Button
              onClick={() => report?.auditTrailId && downloadReport(report.auditTrailId)}
              size="sm"
              variant="ghost"
              disabled={isDownloading || !report?.auditTrailId}
            >
              {isDownloading ? (
                <>
                  <Icon className="lucide--loader-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Icon className="lucide--download" />
                  Download report
                </>
              )}
            </Button>
          </div>

          {/* Results table: sticky header + scrollable body */}
          <div className={cn('min-h-0 overflow-auto rounded-lg', filteredRows.length === 0 ? '' : 'border')}>
            {isReportLoading ? (
              <div className="flex h-full items-center justify-center gap-2 py-16 text-sm text-gray-400">
                <Icon className="lucide--loader animate-spin ease-in-out" />
                Loading results...
              </div>
            ) : report?.isExpired ? (
              <div className="flex h-full items-center justify-center py-16 text-center text-sm text-gray-400">
                {report.auditTrailId
                  ? 'The in-app report has expired. You can still download the full report.'
                  : 'The report has expired and is no longer available to download.'}
              </div>
            ) : isReportError ? (
              <div className="flex h-full items-center justify-center py-16 text-center text-sm text-red-600">
                The report could not be loaded. Please try again.
              </div>
            ) : filteredRows.length === 0 ? (
              <div className="flex h-full items-center justify-center py-16 text-sm text-gray-400">
                No rows to show.
              </div>
            ) : (
              <Table className="table-fixed">
                <TableHeader className="sticky top-0 z-10 bg-gray-50">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className={cn(
                            'h-auto border-r px-3 py-2.5 text-xs font-medium whitespace-nowrap last:border-r-0',
                            (header.column.columnDef.meta as { className?: string } | undefined)?.className,
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id} className="border-r px-3 py-2 align-top text-sm last:border-r-0">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          {report?.truncated && (
            <p className="text-center text-xs text-gray-400">
              Showing the first {(report.rows.length ?? 0).toLocaleString()} rows. Download the report for the full
              list.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A value clamped to its cell width, with the full value shown on hover via the
 * native `title` attribute. We deliberately avoid a Radix tooltip here: this table
 * can render tens of thousands of rows, and one tooltip state machine per cell was
 * the dominant performance cost. `title` gives the same hover affordance for free.
 */
function TruncatedCell({ value, className }: { value: string; className?: string }) {
  if (!value) return <span className="text-gray-300">—</span>;
  return (
    <span className={cn('block truncate', className)} title={value}>
      {value}
    </span>
  );
}
